import { useEffect, useRef, useState } from "react";
import { Stack } from "@mui/material";

import { toast } from "react-toastify";
import {
  CRMData,
  CRMListItem,
  MarketoLoginData,
} from "../../../../../types/apiTypes";
import {
  useCompleteHubspotIntegrationMutation,
  useCompleteSalesforceIntegrationMutation,
  useCreateMarketoIntegrationMutation,
  useImportDataFromCRMMutation,
  useLazyGetCRMListByCampaignQuery,
  useLazyGetMarketoListQuery,
  useLazyVerifyIntegrationTokenQuery,
} from "../../../../../api";
import { Integration } from "../../../../../types/enums";
import { CustomToast } from "../../../../ui/CustomToast";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { SyncCRMModal } from "../../../../ui/modal/SyncCRMModal/SyncCRMModal";
import { useParams } from "react-router-dom";
import { CustomLoader } from "../../../../ui/CustomLoader";
import { useStateWithSearchParams } from "../../../../../hooks";
import { NotificationMessageBlock } from "./NotificationMessageBlock";
import { MarketoFormSection } from "../../../../marketo/MarketoFormSection";

type Field = keyof Omit<MarketoLoginData, "token">;

const validation: Record<Field, (value: string) => boolean> = {
  munchkin_id: (v) => !v.trim(),
  client_id: (v) => !v.trim(),
  client_secret: (v) => !v.trim(),
};

const isKeyOfValidation = (field: string): field is Field => {
  return field in validation;
};

const safeParseState = (state: string) => {
  try {
    return JSON.parse(atob(decodeURIComponent(state)));
  } catch {
    try {
      return JSON.parse(atob(state));
    } catch {
      return null;
    }
  }
};

export const CRMPageBody = () => {
  const { campaign_id } = useParams();
  const { searchDict } = useStateWithSearchParams<{
    token?: string;
    code?: string;
    state?: string;
    platform?: string;
    error?: string;
  }>({
    token: "",
    code: "",
    state: "",
    platform: "",
    error: "",
  });

  let platformFromState = searchDict.platform;
  if (!platformFromState && searchDict.state) {
    const parsed = safeParseState(searchDict.state);
    platformFromState = parsed?.platform;
  }

  const platform = (platformFromState || Integration.marketo) as Integration;
  const isMarketo = platform === Integration.marketo;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSynced, setIsSynced] = useState<boolean>(false);
  const [isAlreadyConnected, setIsAlreadyConnected] = useState<boolean>(false);
  const [isNoValid, setIsNoValid] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(true);
  const [resolvedCampaignId, setResolvedCampaignId] = useState<string>(
    campaign_id || "",
  );
  const [crmList, setCrmList] = useState<CRMListItem[]>([]);
  const [formData, setFormData] = useState<MarketoLoginData>({
    munchkin_id: "",
    client_id: "",
    client_secret: "",
  });
  const [errors, setErrors] = useState<Record<Field, boolean>>({
    munchkin_id: false,
    client_id: false,
    client_secret: false,
  });

  const [
    createMarketoIntegration,
    { isLoading: isCreatingMarketoIntegration },
  ] = useCreateMarketoIntegrationMutation();
  const [getMarketoList, { isLoading: isListLoading, data: marketoListData }] =
    useLazyGetMarketoListQuery();
  const [verifyIntegrationToken] = useLazyVerifyIntegrationTokenQuery();
  const [importFromCRM, { isLoading: isImportingFromCRM }] =
    useImportDataFromCRMMutation();
  const [completeHubspotIntegration, { isLoading: isCompletingHubspot }] =
    useCompleteHubspotIntegrationMutation();
  const [completeSalesforceIntegration, { isLoading: isCompletingSalesforce }] =
    useCompleteSalesforceIntegrationMutation();
  const [getCRMListByCampaign, { isLoading: isCRMListLoading }] =
    useLazyGetCRMListByCampaignQuery();

  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    if (isMarketo) {
      verifyIntegrationToken({
        token: searchDict?.token || "",
        platform: Integration.marketo,
      })
        .unwrap()
        .catch((_) => {
          setIsNoValid(true);
        })
        .finally(() => setIsVerifying(false));
    } else {
      if (searchDict.error === "invalid_token") {
        setIsNoValid(true);
        setIsVerifying(false);
        return;
      }

      if (!searchDict.code || !searchDict.state) {
        setIsNoValid(true);
        setIsVerifying(false);
        return;
      }

      const parsed = safeParseState(searchDict.state);

      if (!parsed) {
        setIsNoValid(true);
        setIsVerifying(false);
        return;
      }

      setResolvedCampaignId(parsed.campaign_id);

      const completeFn =
        platform === Integration.hubspot
          ? completeHubspotIntegration
          : completeSalesforceIntegration;

      completeFn({ token: parsed.token, code: searchDict.code })
        .unwrap()
        .then(() => {
          getCRMListByCampaign({
            campaign_id: parsed.campaign_id,
            crm: platform,
          })
            .unwrap()
            .then((data) => {
              setCrmList(data || []);
            })
            .finally(() => {
              setIsOpen(true);
              setIsVerifying(false);
            });
        })
        .catch((err) => {
          if (err?.status === 409 || err?.data?.detail?.includes("already")) {
            setIsAlreadyConnected(true);
          } else {
            setIsNoValid(true);
          }
          setIsVerifying(false);
        });
    }
  }, []);

  const isLoading = isCreatingMarketoIntegration || isListLoading;
  const isOAuthLoading =
    isCompletingHubspot || isCompletingSalesforce || isCRMListLoading;

  const inputChangeHandler = (field: string, value: string) => {
    if (isKeyOfValidation(field)) {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: validation[field](value) }));
    }
  };

  const submitHandler = () => {
    if (!campaign_id) return;

    createMarketoIntegration({ ...formData, token: searchDict.token })
      .unwrap()
      .then(() => {
        getMarketoList(campaign_id)
          .unwrap()
          .catch((err) => {
            toast.error(
              <CustomToast
                type="error"
                text={
                  getErrorMessage(err).globalError ||
                  "Something went wrong. Please try again"
                }
                title="Failed Sync with Marketo"
              />,
            );
          });
        setIsOpen(true);
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title="Failed to Make Integration with Marketo"
          />,
        );
      });
  };

  const submitCRMHandler = async (data: CRMData) => {
    const list_id = data?.list_id?.length ? data.list_id : undefined;
    const cid = isMarketo ? campaign_id! : resolvedCampaignId;

    await importFromCRM({
      campaign_id: cid,
      import_contacts: true,
      crm: platform,
      list_id,
    })
      .unwrap()
      .then(() => setIsSynced(true))
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title="Data Is Not Synced"
          />,
        );
      });
  };

  if (isVerifying) return <CustomLoader />;

  if (isSynced || isNoValid || isAlreadyConnected) {
    return (
      <Stack alignItems="center" justifyContent="center" flexGrow={1}>
        <NotificationMessageBlock
          isCompleted={isSynced}
          isAlreadyConnected={isAlreadyConnected}
          platform={platform}
        />
      </Stack>
    );
  }

  if (!isMarketo) {
    return (
      <Stack alignItems="center" justifyContent="center" flexGrow={1}>
        {(isOpen || importFromCRM) && (
          <SyncCRMModal
            crmList={crmList}
            openModal={platform}
            onClose={() => setIsOpen(false)}
            setDataSource={submitCRMHandler}
            setOpenModal={() => {}}
            isLoading={isOAuthLoading || isImportingFromCRM}
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" justifyContent="center" flexGrow={1}>
      <MarketoFormSection
        formData={formData}
        errors={errors}
        isLoading={isLoading}
        isCreatingMarketoIntegration={isCreatingMarketoIntegration}
        onInputChange={inputChangeHandler}
        onSubmit={submitHandler}
      />

      {(isOpen || isImportingFromCRM) && (
        <SyncCRMModal
          crmList={marketoListData || []}
          openModal={Integration.marketo}
          onClose={() => setIsOpen(false)}
          setDataSource={submitCRMHandler}
          setOpenModal={() => setIsOpen(false)}
          isLoading={isLoading || isImportingFromCRM}
        />
      )}
    </Stack>
  );
};
